/**
 * ProviderExamsPage
 * Page for managing which exams a provider can perform.
 * Exams are added via autocomplete and edited inline in a table,
 * then persisted in bulk via PUT /provider-exams/:providerId/bulk.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Button,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Save as SaveIcon, FileDownload as FileDownloadIcon, FileUpload as FileUploadIcon, Percent as PercentIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { PageShell } from '../../components';
import ProviderExamsTable from './components/ProviderExamsTable';
import ProviderExamsImportDialog from './components/ProviderExamsImportDialog';
import ProviderPriceAdjustmentDialog, { PriceAdjustmentValues } from './components/ProviderPriceAdjustmentDialog';
import { useProviderExams } from './hooks/useProviderExams';
import { useAcao } from '../../authorization/useVeredito';
import { useDadoPermitido } from '../../authorization/useListaPermitida';
import { ActionButton } from '../../components/common';
import { useProviderExamExport } from '../../hooks/useProviderExamExport';
import { providerService } from '../../services/providerService';
import { Provider } from '../../types/provider';
import { ProviderAvailableExam } from '../../types/providerExam';
import {
  EditableExamRow,
  rowFromLinked,
  rowFromAvailable,
  rowsToSave,
  collectBulkItems,
  validateRow,
  insertSorted,
} from './providerExamRows';

const ProviderExamsPage: React.FC = () => {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const adicionar = useAcao('provider_exam.create', 'Adicionar exame');
  const editarExames = useAcao('provider_exam.update', 'Editar exames');
  const desvincular = useAcao('provider_exam.delete', 'Desvincular exame');

  const {
    linked,
    available,
    loading,
    error,
    loadExams,
    bulkUpsert,
    unlinkSingle,
    clearError,
  } = useProviderExams();


  const [rows, setRows] = useState<EditableExamRow[]>([]);
  const [options, setOptions] = useState<ProviderAvailableExam[]>([]);
  const [removeTarget, setRemoveTarget] = useState<EditableExamRow | null>(null);
  const { exporting, exportLinked } = useProviderExamExport();
  const [importOpen, setImportOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [savingAdjust, setSavingAdjust] = useState(false);

  const numericProviderId = providerId ? parseInt(providerId, 10) : NaN;

  // O cabeçalho é dado do prestador, e ler prestador é outra permissão: quem só administra os
  // exames dele era expulso para Prestadores, uma tela que também não abre para essa pessoa.
  // A tela continua de pé sem o cabeçalho — os exames são o conteúdo dela.
  const carregarPrestador = useCallback(
    () => providerService.getProviderById(String(numericProviderId)),
    [numericProviderId]
  );
  const prestador = useDadoPermitido<Provider>('provider.read', 'Prestador', carregarPrestador, {
    habilitado: !isNaN(numericProviderId),
  });
  const provider = prestador.dado;
  const providerLoading = prestador.carregando;

  useEffect(() => {
    if (isNaN(numericProviderId)) {
      navigate('/providers');
      return;
    }
    loadExams(numericProviderId);
  }, [numericProviderId, navigate, loadExams]);

  useEffect(() => {
    setRows(linked.map(rowFromLinked));
    setOptions(available);
  }, [linked, available]);

  const handleAdd = (exam: ProviderAvailableExam | null) => {
    if (!exam) return;
    setRows((prev) => insertSorted(prev, rowFromAvailable(exam)));
    setOptions((prev) => prev.filter((o) => o.id !== exam.id));
  };

  const handleChange = (examId: number, patch: Partial<EditableExamRow>) =>
    setRows((prev) => prev.map((r) => r.examId === examId
      ? { ...r, ...patch, _dirty: r._origin === 'existing' ? true : r._dirty } : r));

  const handleRemove = (row: EditableExamRow) => {
    if (row._origin === 'new') {
      setRows((prev) => prev.filter((r) => r.examId !== row.examId));
      setOptions((prev) => [
        { id: row.examId, examName: row.examName, esocialCode: row.esocialCode, tussCode: row.tussCode, defaultFrequency: null },
        ...prev,
      ]);
    } else {
      setRemoveTarget(row);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    const target = removeTarget;
    const ok = await unlinkSingle(numericProviderId, target.examId);
    if (ok) {
      setRows((prev) => prev.filter((r) => r.examId !== target.examId));
      setOptions((prev) => [
        { id: target.examId, examName: target.examName, esocialCode: target.esocialCode, tussCode: target.tussCode, defaultFrequency: null },
        ...prev,
      ]);
      enqueueSnackbar('Exame desvinculado', { variant: 'success' });
    }
    setRemoveTarget(null);
  };

  const pending = rowsToSave(rows);

  const handleSaveAll = async () => {
    const invalid = pending.find((r) => validateRow(r));
    if (invalid) {
      enqueueSnackbar(`Verifique "${invalid.examName}": ${validateRow(invalid)}`, { variant: 'warning' });
      return;
    }
    const result = await bulkUpsert(numericProviderId, collectBulkItems(rows));
    if (!result) return;
    if (result.failed.length === 0) {
      enqueueSnackbar('Exames salvos com sucesso', { variant: 'success' });
    } else {
      enqueueSnackbar(`${result.success.length} salvos, ${result.failed.length} com erro`, { variant: 'warning' });
    }
  };

  const handleSaveAdjustment = async (values: PriceAdjustmentValues) => {
    setSavingAdjust(true);
    try {
      await providerService.updateProvider(String(numericProviderId), values);
      prestador.recarregar();
      setAdjustOpen(false);
      enqueueSnackbar('Reajuste salvo no cadastro do prestador', { variant: 'success' });
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao salvar reajuste', { variant: 'error' });
    } finally {
      setSavingAdjust(false);
    }
  };

  if (providerLoading) {
    return (
      <PageShell
        breadcrumbs={[
          { label: 'Início', path: '/' },
          { label: 'Prestadores', path: '/providers' },
          { label: 'Exames' },
        ]}
        title="Exames do Prestador"
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  const providerName = provider?.tradeName || provider?.legalName || 'Prestador';
  const avisoDoCabecalho = prestador.bloqueio ?? prestador.erro;
  const tableDisabled = loading || (adicionar.disabled && editarExames.disabled);

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Prestadores', path: '/providers' },
        { label: providerName, path: `/providers/${providerId}` },
        { label: 'Exames' },
      ]}
      title="Exames do Prestador"
    >
        <Box sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {providerName}
          </Typography>
          {provider?.cnpj && (
            <Chip label={provider.cnpj} size="small" variant="outlined" />
          )}
          {provider?.providerType && (
            <Chip label={provider.providerType} size="small" color="primary" variant="outlined" />
          )}
          {avisoDoCabecalho && (
            <Typography variant="caption" color="text.secondary">
              {avisoDoCabecalho}
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <Autocomplete
              sx={{ flex: 1 }}
              options={options}
              getOptionLabel={(o) => o.examName}
              filterOptions={(opts, state) => {
                const q = state.inputValue.trim().toLowerCase();
                if (!q) return opts;
                return opts.filter((o) =>
                  o.examName.toLowerCase().includes(q) ||
                  (o.esocialCode ?? '').toLowerCase().includes(q) ||
                  (o.tussCode ?? '').toLowerCase().startsWith(q));
              }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={null}
              disabled={adicionar.disabled || loading}
              onChange={(_e, value) => handleAdd(value)}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                const meta = [
                  option.esocialCode && `eSocial: ${option.esocialCode}`,
                  option.tussCode && `TUSS: ${option.tussCode}`,
                ].filter(Boolean).join(' · ');
                return (
                  <li key={key} {...optionProps}>
                    <Box>
                      <Typography variant="body2">{option.examName}</Typography>
                      {meta && (
                        <Typography variant="caption" color="text.secondary">{meta}</Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label="Adicionar exame…" size="small" placeholder="Nome, código eSocial ou TUSS" />
              )}
            />
            <ActionButton
              acao={['provider_exam.create', 'provider_exam.update']}
              rotulo="Salvar tudo"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={pending.length === 0 || loading}
              onClick={handleSaveAll}
            >
              Salvar tudo ({pending.length})
            </ActionButton>
            <ActionButton
              acao="provider_exam.read"
              rotulo="Exportar"
              variant="outlined" startIcon={<FileDownloadIcon />}
              disabled={exporting || loading}
              onClick={() => exportLinked(numericProviderId, `prestador-${numericProviderId}-exames.xlsx`)}
            >
              Exportar
            </ActionButton>
            <ActionButton
              acao={['provider_exam.create', 'provider_exam.update']}
              rotulo="Importar"
              variant="outlined" startIcon={<FileUploadIcon />}
              disabled={loading}
              onClick={() => {
                if (pending.length > 0) {
                  enqueueSnackbar('Há alterações não salvas na tabela. Salve ou recarregue antes de importar.', { variant: 'warning' });
                  return;
                }
                setImportOpen(true);
              }}
            >
              Importar
            </ActionButton>
            <ActionButton
              acao="provider.update"
              rotulo="Reajuste"
              variant="outlined"
              startIcon={<PercentIcon />}
              disabled={loading}
              onClick={() => setAdjustOpen(true)}
            >
              Reajuste
            </ActionButton>
            {loading && <CircularProgress size={24} />}
          </Box>

          <ProviderExamsTable
            rows={rows}
            disabled={tableDisabled}
            canRemove={!desvincular.disabled}
            onChange={handleChange}
            onRemove={handleRemove}
          />
        </Paper>

        <Dialog open={removeTarget != null} onClose={() => setRemoveTarget(null)}>
          <DialogTitle>Desvincular exame</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja desvincular &quot;{removeTarget?.examName}&quot; deste prestador?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoveTarget(null)} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={confirmRemove} color="error" variant="contained" disabled={loading || desvincular.disabled}>
              Desvincular
            </Button>
          </DialogActions>
        </Dialog>

        <ProviderExamsImportDialog
          open={importOpen}
          providerId={numericProviderId}
          onClose={() => setImportOpen(false)}
          onImported={() => loadExams(numericProviderId)}
        />

        <ProviderPriceAdjustmentDialog
          open={adjustOpen}
          initialDate={provider?.priceAdjustmentDate ? String(provider.priceAdjustmentDate).slice(0, 10) : ''}
          initialPercentage={provider?.priceAdjustmentPercentage != null ? String(provider.priceAdjustmentPercentage) : ''}
          saving={savingAdjust}
          onClose={() => setAdjustOpen(false)}
          onSave={handleSaveAdjustment}
        />
    </PageShell>
  );
};

export default ProviderExamsPage;
